import { useState, useEffect } from 'react'
import Seo from '../components/Seo.jsx'
import Reveal from '../components/Reveal.jsx'
import { Link } from 'react-router-dom'

// Import local assets
import manoj from '../assets/manoj.webp'
import sneha from '../assets/sneha.webp'

// Import team showcase photos for Chapter 05
import team1 from '../assets/team-1.jpg'
import team2 from '../assets/Team-2.jpg'
import team3 from '../assets/Team-3.jpg'
import team4 from '../assets/Team-4.webp'
import team5 from '../assets/Team-5.jpg'
import team6 from '../assets/Team-6.jpg'
import aboutVisionImg from '../assets/about-1-web.jpg'
import aboutMissionImg from '../assets/about-2-web.jpg'
import aboutSpeedImg from '../assets/about-3-web.jpg'
import aboutPriceImg from '../assets/about-4-web.jpg'
import aboutHandoverImg from '../assets/about-5-web.jpg'
import aboutStackImg from '../assets/about-6-web.jpg'
import { apiAsset } from '../lib/apiAsset.js'

/* =========================================================================
   ABOUT — Spectranox (chapter-style narrative)
   Alternating light / cream / dark sections, each marked as a numbered
   "chapter", so the page reads as a story rather than a stack of panels.
   ========================================================================= */

/* ----------------------------- Atoms ----------------------------------- */
const Chapter = ({ number, label, invert = false }) => (
  <div className={'flex items-center justify-center gap-3 ' + (invert ? 'text-[#52b4ff]' : 'text-[#0062ad]')}>
    <span className={'h-px w-10 ' + (invert ? 'bg-cyan-400/60' : 'bg-[#0079d6]/70')} />
    <span className="font-mono text-[11px] font-medium uppercase tracking-[0.28em]">
      Chapter {number} <span className={invert ? 'text-slate-500' : 'text-slate-400'}>·</span> {label}
    </span>
    <span className={'h-px w-10 ' + (invert ? 'bg-cyan-400/60' : 'bg-[#0079d6]/70')} />
  </div>
)

const Icon = ({ name, className = 'h-5 w-5' }) => {
  const paths = {
    arrow: <path d="M5 12h14M13 5l7 7-7 7" />,
    check: <path d="M5 12l4 4L19 7" />,
    quote: <path d="M7 7c-2 1-3 3-3 6v4h6v-7H7zm10 0c-2 1-3 3-3 6v4h6v-7h-3z" />,
    heart: <path d="M20.8 4.6a5.5 5.5 0 00-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 00-7.8 7.8l8.8 8.8 8.8-8.8a5.5 5.5 0 000-7.8z" />,
    shield: <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z" />,
    bolt: <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />,
    rupee: <path d="M6 3h12M6 8h12M9 21l-3-8c4 0 7-1 7-5" />,
    coffee: <><path d="M3 8h14v8a4 4 0 01-4 4H7a4 4 0 01-4-4z" /><path d="M17 10h2a2 2 0 010 4h-2" /><path d="M6 2v3M10 2v3M14 2v3" /></>,
    sparkle: <path d="M12 3l1.7 5.3L19 10l-5.3 1.7L12 17l-1.7-5.3L5 10l5.3-1.7L12 3z" />,
    play: <path d="M6 4l14 8-14 8V4z" />,
  }
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  )
}

const ChapterHead = ({ number, label, title, accent = 'text-[#0062ad]', invert = false }) => {
  const parts = title.split('. ')
  return (
    <div className="mx-auto max-w-3xl text-center">
      <Chapter number={number} label={label} invert={invert} />
      <h2 className={'mt-6 font-display text-[clamp(2rem,3.6vw,3rem)] font-bold leading-[1.1] tracking-tight ' + (invert ? 'text-white' : 'text-slate-900')}>
        {parts[0]}.
        {parts[1] && (
          <>
            <br />
            <span className={accent}>{parts[1]}</span>
          </>
        )}
      </h2>
    </div>
  )
}

/* =========================== CHAPTER 02 — Founder ======================== */
function Founder() {
  return (
    <section id="founder" className="relative bg-[#fdf8f0] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="relative lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden rounded-3xl border-4 border-white bg-slate-100 shadow-xl">
              <img src={manoj} alt="Founder portrait" className="h-full w-full object-cover" />
              <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-md bg-white/95 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[#0062ad] shadow">
                Founder
              </span>
            </div>
            <div className="mt-5 text-center">
              <div className="font-display text-xl font-bold text-slate-900">Manoj Koravangi</div>
              <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">Founder &amp; CEO · Cyber-security specialist</div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {/* Chapter label + title (left-aligned, lives with the body) */}
            <div className="flex items-center gap-3 text-[#0062ad]">
              <span className="h-px w-10 bg-[#0079d6]/70" />
              <span className="font-mono text-[11px] font-medium uppercase tracking-[0.28em]">
                Chapter 02 <span className="text-slate-400">·</span> The Founder
              </span>
            </div>
            <h2 className="mt-4 font-display text-[clamp(2rem,3.6vw,3rem)] font-bold leading-[1.1] tracking-tight text-slate-900">
              Pushed into civil.
              <br />
              <span className="text-[#0062ad]">Pulled to cyber. How a college dropout built a tech company.</span>
            </h2>

            <p className="mt-8 text-base leading-relaxed text-slate-700">
              Manoj was pushed into <span className="font-semibold text-slate-900">civil engineering</span>.
              But his heart was always in computers, and you can't fake what you love.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-700">
              So he bet on himself. He taught himself the craft, <span className="font-semibold text-slate-900">dropped out</span>,
              and walked straight into cyber security, turning a forced detour into the work he was born to do.
            </p>

            <figure className="mt-8 rounded-2xl border-l-4 border-[#008eff] bg-white p-6 shadow-sm">
              <blockquote className="font-display text-lg italic text-slate-900">
                "My father enrolled me in civil engineering. I enrolled myself in cyber-security, and
                stopped showing up to class to learn it. Dropping out felt like the most honest thing I'd done."
              </blockquote>
              <figcaption className="mt-4 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Manoj Koravangi · Founder &amp; CEO
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
  )
}

/* =========================== CHAPTER 03 — Co-founder ===================== */
function Cofounder() {
  return (
    <section className="relative bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            {/* Chapter label + title (left-aligned, lives with the body) */}
            <div className="flex items-center gap-3 text-[#0062ad]">
              <span className="h-px w-10 bg-[#0079d6]/70" />
              <span className="font-mono text-[11px] font-medium uppercase tracking-[0.28em]">
                Chapter 03 <span className="text-slate-400">·</span> The Co-founder
              </span>
            </div>
            <h2 className="mt-4 font-display text-[clamp(2rem,3.6vw,3rem)] font-bold leading-[1.1] tracking-tight text-slate-900">
              The quiet force behind the big calls.
              <br />
              <span className="text-[#0062ad]">Meet Sneha.</span>
            </h2>

            <p className="mt-8 text-base leading-relaxed text-slate-700">
              Manoj brings the vision and the energy. Sneha Mogilicherla brings the perspective that
              keeps it all pointed the right way. As Co-founder &amp; Strategic Advisor she works mostly
              out of sight, never in the day-to-day noise, never far from the decisions that matter.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-700">
              She's the sounding-board the team leans on for the hard calls, weighing direction,
              shipping decisions, spotting the risks no one else catches, and quietly shaping where
              Spectranox heads next. Less noise, more clarity.
            </p>

            <dl className="mt-8 grid grid-cols-2 gap-5">
              <div className="rounded-2xl bg-slate-50 p-5">
                <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0062ad]">Strength</dt>
                <dd className="mt-2 font-display text-base font-semibold text-slate-900">Commercial direction</dd>
              </div>
              <div className="rounded-2xl bg-slate-50 p-5">
                <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0062ad]">Strength</dt>
                <dd className="mt-2 font-display text-base font-semibold text-slate-900">Partnership strategy</dd>
              </div>
            </dl>

            <figure className="mt-8 rounded-2xl border-l-4 border-amber-400 bg-amber-50/60 p-6">
              <blockquote className="font-display text-base italic text-slate-900">
                "My job isn't to be in every room. It's to ask the one question everyone forgot to ask,
                and to make sure the big bets are the right calls."
              </blockquote>
              <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Sneha Mogilicherla · Co-founder &amp; Strategic Advisor
              </figcaption>
            </figure>
          </div>

          <div className="relative lg:col-span-5 w-full">
            <div className="relative aspect-[4/5] overflow-hidden rounded-3xl border-4 border-white bg-slate-100 shadow-xl">
              <img src={sneha} alt="Co-founder portrait" className="h-full w-full object-cover" />
              <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-md bg-[#008eff] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-900 shadow">
                Co-founder
              </span>
            </div>
            <div className="mt-5 text-center">
              <div className="font-display text-xl font-bold text-slate-900">Sneha Mogilicherla</div>
              <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">Co-founder &amp; Strategic Advisor</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ==================== CHAPTER 01 — Vision and mission ==================== */
/* Sits directly after the two founders: who started it, then what they are
   trying to build, then the team who does the building. */
function VisionMission() {
  /* Each pillar carries its own accent, as in the reference: the icon chip,
     the hairline under the artwork and the hover ring all take it. */
  const pillars = [
    {
      icon: 'bolt',
      img: aboutSpeedImg,
      accent: '#38bdf8',
      k: 'Speed without shortcuts',
      d: 'Forty-five days to production, with tests, monitoring and handover docs.',
    },
    {
      icon: 'rupee',
      img: aboutPriceImg,
      accent: '#a78bfa',
      k: 'Enterprise work at a startup price',
      d: 'Up to 90% below a traditional agency build, because senior engineers who care.',
    },
    {
      icon: 'shield',
      img: aboutHandoverImg,
      accent: '#34d399',
      k: 'Built to be handed over',
      d: 'Code, cloud and IP are yours at launch. Take it in-house or anywhere.',
    },
    {
      icon: 'sparkle',
      img: aboutStackImg,
      accent: '#c084fc',
      k: 'One team, whole stack',
      d: 'Web, apps, AI, IoT, drones and security under one roof. No handoffs. No gaps.',
    },
  ]

  return (
    <section className="relative overflow-hidden bg-[#050c1a] pb-24 pt-14 text-white lg:pb-32 lg:pt-16">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #38bdf8 1px, transparent 0)',
          backgroundSize: '34px 34px',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-28 right-[-8%] h-96 w-[42rem] rounded-full blur-[150px]"
        style={{ background: 'radial-gradient(circle, rgba(0,142,255,0.22), transparent 62%)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-10rem] left-[-6%] h-96 w-[38rem] rounded-full blur-[150px]"
        style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.18), transparent 62%)' }}
      />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <ChapterHead
          number="01"
          label="Vision &amp; mission"
          title="Where we are going. And what we do about it daily"
          accent="text-[#52b4ff]"
          invert
        />

        {/* Vision and mission: copy on the left of each card, artwork bleeding
            off its right edge. */}
        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {[
            {
              label: 'Our vision',
              accent: '#38bdf8',
              chipBg: 'rgba(56,189,248,0.14)',
              chipRing: 'rgba(56,189,248,0.32)',
              img: aboutVisionImg,
              icon: (
                <><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="3" /></>
              ),
              head: (
                <>
                  That no great business ever has to lose to a{' '}
                  {/* The reference sets the accented phrase on its own line. */}
                  <span className="block" style={{ color: '#4da6ff' }}>bad build.</span>
                </>
              ),
              body: [
                'Serious software has been priced and paced for large companies for too long. We believe great ideas deserve engineering that’s accessible, fast, and built to last.',
                'We bring enterprise-grade engineering within reach of teams of any size—so you can move faster, serve better, and scale with confidence.',
              ],
            },
            {
              label: 'Our mission',
              accent: '#34d399',
              chipBg: 'rgba(52,211,153,0.14)',
              chipRing: 'rgba(52,211,153,0.32)',
              img: aboutMissionImg,
              icon: (
                <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" /></>
              ),
              head: (
                <>
                  Ship production software in{' '}
                  <span style={{ color: '#34d399' }}>45 days</span>, and stand behind it.
                </>
              ),
              body: [
                'We build it fast. We build it right. Then we stay with you—patching, improving, and evolving it for a whole year because the work isn’t done until it delivers real impact.',
                'From day one, you get senior engineers, clear communication, and solutions that are built for performance, security, and scale.',
              ],
            },
          ].map((c) => (
            <article
              key={c.label}
              className="relative w-full overflow-hidden rounded-3xl border border-white/10 bg-white/[0.035] backdrop-blur"
              style={{ containerType: 'inline-size' }}
            >
              {/* On a phone the artwork is a band across the top of the card;
                  from sm it floats to the right of the copy, feathered by a
                  radial mask. The mask is a class rather than an inline style
                  so it can be dropped at the narrow size, where a feathered
                  band would read as a mistake. */}
              <img
                src={c.img}
                alt=""
                aria-hidden="true"
                width={900}
                height={900}
                loading="lazy"
                decoding="async"
                className="block h-52 w-full select-none object-cover object-[center_30%] sm:pointer-events-none sm:absolute sm:inset-y-0 sm:right-0 sm:h-full sm:w-[46%] sm:[-webkit-mask-image:linear-gradient(90deg,transparent_0%,rgba(0,0,0,0.55)_22%,#000_44%)] sm:[mask-image:linear-gradient(90deg,transparent_0%,rgba(0,0,0,0.55)_22%,#000_44%)]"
              />

              <div className="relative p-6 sm:max-w-[62%] sm:px-[clamp(22px,5.2cqw,40px)] sm:py-[clamp(18px,3.6cqw,28px)]">
                <span
                  className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-mono text-[clamp(9px,1.5cqw,11px)] font-bold uppercase tracking-[0.2em]"
                  style={{ background: c.chipBg, color: c.accent, boxShadow: `inset 0 0 0 1px ${c.chipRing}` }}
                >
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    {c.icon}
                  </svg>
                  {c.label}
                </span>

                <h3 className="mt-[2.73cqw] font-display text-[clamp(1.25rem,4.23cqw,1.95rem)] font-bold leading-[1.4] tracking-tight">
                  {c.head}
                </h3>

                <span aria-hidden className="mt-[2.05cqw] block h-[3px] w-[clamp(26px,4.6cqw,40px)] rounded-full" style={{ background: c.accent }} />

                {c.body.map((t) => (
                  <p key={t.slice(0, 28)} className="mt-[2.6cqw] text-[clamp(11px,1.78cqw,13px)] leading-[1.82] text-slate-300">
                    {t}
                  </p>
                ))}
              </div>
            </article>
          ))}
        </div>

        {/* The four commitments that turn the mission into something checkable */}
        <div className="mt-6 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {pillars.map((v) => (
            <article
              key={v.k}
              className="group relative flex w-full flex-col overflow-hidden rounded-2xl transition-all duration-500 hover:-translate-y-1"
              style={{
                containerType: 'inline-size',
                background: `linear-gradient(165deg, ${v.accent}1a 0%, rgba(255,255,255,0.03) 42%, rgba(255,255,255,0.02) 100%)`,
                boxShadow: `inset 0 0 0 1px ${v.accent}30`,
              }}
            >
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                style={{ boxShadow: `inset 0 0 0 1px ${v.accent}80, 0 0 34px -8px ${v.accent}55` }}
              />

              <div className="relative aspect-[2.48/1] overflow-hidden">
                <img
                  src={v.img}
                  alt=""
                  aria-hidden="true"
                  width={820}
                  height={461}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full select-none object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* Fades the artwork into the card rather than ending it on a line. */}
                <span
                  aria-hidden
                  className="absolute inset-x-0 bottom-0 h-2/3"
                  style={{ background: `linear-gradient(180deg, rgba(6,14,28,0) 0%, rgba(6,14,28,0.70) 60%, ${v.accent}14 100%), linear-gradient(180deg, rgba(6,14,28,0) 55%, #070f1e 100%)` }}
                />
              </div>

              <div className="relative flex flex-1 flex-col p-[clamp(18px,8cqw,30px)] pt-4">
                <h4 className="font-display text-[clamp(15px,6.05cqw,22px)] font-bold leading-[1.25] tracking-tight pr-5">{v.k}</h4>
                <p className="mt-2.5 flex-1 pr-[20%] text-[clamp(11.5px,3.75cqw,13px)] leading-[1.77] text-slate-400">{v.d}</p>
                <span
                  className="absolute bottom-6 right-6 inline-flex h-10 w-10 items-center justify-center rounded-xl"
                  style={{ background: `${v.accent}1f`, color: v.accent, boxShadow: `inset 0 0 0 1px ${v.accent}3d` }}
                >
                  <Icon name={v.icon} className="h-4.5 w-4.5" />
                </span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

/* =========================== CHAPTER 04 — Team =========================== */
function Team() {
  const [teamList, setTeamList] = useState([])

  useEffect(() => {
    const API_BASE = import.meta.env.VITE_API_BASE || '/api'
    fetch(`${API_BASE}/get-team.php`)
      .then((res) => res.json())
      .then((data) => {
        if (data.ok && data.sections && data.sections.team && data.sections.team.length > 0) {
          setTeamList(data.sections.team)
        }
      })
      .catch((err) => console.error('Failed to load team list:', err))
  }, [])

  /* No local fallback roster. The previous one listed eight invented people
     with portraits pulled from a public avatar service, which is a real
     person's face attached to a name that does not work here. If the API has
     nothing, the section says nothing. */

  const displayTeam = teamList.map((m) => ({
    name: m.title,
    role: m.subtitle,
    tag: 'TEAM MEMBER',
    img: apiAsset(m.imageUrl),
    quote: m.body,
  }))

  return (
    <section className="relative bg-[#fdf8f0] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <ChapterHead number="04" label="The Team" title="Many roles. One mission" />
        <p className="mx-auto mt-5 max-w-2xl text-center text-base text-slate-600">
          Engineers, designers, hardware makers, security analysts and project leads.
          Everyone here builds something a client actually uses, and every one of them
          sits in the calls where their work gets decided.
        </p>

        {displayTeam.length === 0 && (
          <div className="mx-auto mt-12 max-w-lg rounded-2xl border border-dashed border-slate-300 py-12 text-center">
            <p className="text-sm font-semibold text-slate-700">Team profiles are being updated</p>
            <p className="mt-2 text-sm text-slate-500">
              Ask us who would be on your project and we will introduce them by name.
            </p>
          </div>
        )}

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {displayTeam.map((m) => (
            <article key={m.name} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={m.img} alt={m.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" referrerPolicy="no-referrer" />
                <span className="absolute right-3 top-3 rounded-md bg-white/95 px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-wider text-slate-700 backdrop-blur">
                  {m.tag}
                </span>
                <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 55%, rgba(15,23,42,0.85) 100%)' }} />
                <div className="absolute inset-x-4 bottom-3 text-white">
                  <div className="font-display text-base font-bold">{m.name}</div>
                  <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#52b4ff]">{m.role}</div>
                </div>
              </div>
              <div className="bg-cyan-700/95 p-4 text-[12px] leading-snug text-white">
                <span className="font-mono text-[9px] font-bold uppercase tracking-wider text-cyan-200">★ {teamList.length > 0 ? 'Bio' : 'Two truths and a lie'}</span>
                <p className="mt-2 italic">{m.quote}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

/* =========================== CHAPTER 04 — How we work ==================== */
/* The six terms every engagement runs on. These are the same commitments the
   home page and the service pages make, stated once in full. */
const WORKING_TERMS = [
  { k: 'Scope',     v: 'Fixed price',  d: 'Agreed in writing before a line is written. No change-order creep, no surprise invoice at handover.' },
  { k: 'Access',    v: 'Day one',      d: 'Repository, staging and cloud access from the first week. A working demo every week after that.' },
  { k: 'Delivery',  v: '45 days',      d: 'Average time from signed scope to a build your team is using. Not a prototype, the real thing.' },
  { k: 'Warranty',  v: '30 days',      d: 'Anything we ship that breaks, we repair free. No ticket haggling, no clock-watching.' },
  { k: 'Security',  v: '12 months',    d: 'Patching, monitoring and dependency updates on us for a full year after launch.' },
  { k: 'Ownership', v: '100% yours',   d: 'Code, cloud and IP transferred at launch. No retainer lock-in, no vendor hostage-taking.' },
]

function HowWeWork() {
  return (
    <section className="relative bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <ChapterHead number="05" label="How we work" title="Six terms. Every engagement" />
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600">
          These aren&apos;t aspirations on a culture page. They go into the contract you sign,
          and they are the same terms quoted on every service page of this site.
        </p>

        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WORKING_TERMS.map((t) => (
            <article
              key={t.k}
              className="group relative flex flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-[#008eff]/40 hover:shadow-xl"
            >
              <div className="font-mono text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">
                {t.k}
              </div>
              <div className="mt-2 font-display text-[26px] font-extrabold leading-none tracking-tight text-[#0062ad]">
                {t.v}
              </div>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{t.d}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

/* =========================== CHAPTER 05 — We chill harder ================ */
function WeChillHarder() {
  const photos = [
    { src: team1, label: 'Coding break' },
    { src: team2, label: 'Release nights' },
    { src: team3, label: 'Lunch sides · every day' },
    { src: team4, label: 'Client workshops' },
    { src: team5, label: 'Demos' },
    { src: team6, label: 'The day we shipped' },
  ]
  return (
    <section className="relative bg-[#fdf8f0] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <ChapterHead number="06" label="Behind the work" title="The team. Off the deck" />

        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {photos.map((p) => (
            <figure key={p.label} className="group relative aspect-[4/3] overflow-hidden rounded-2xl shadow-md transition-all hover:-translate-y-1 hover:shadow-xl">
              <img src={p.src} alt={p.label} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 55%, rgba(15,23,42,0.85) 100%)' }} />
              <figcaption className="absolute inset-x-4 bottom-3 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-white">
                {p.label}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  )
}

/* =========================== CHAPTER 07 — Four lines ===================== */
function FourLines() {
  const lines = [
    { icon: 'heart', title: 'Humanity first', body: "Clients aren't tickets. Engineers aren't resources. Vendors aren't disposable. People come before margin, every time." },
    { icon: 'shield', title: 'No false promises', body: "We don't guarantee outcomes we can't control. Every number on this site is real. Every case study is a human you can call." },
    { icon: 'bolt', title: 'Help, then polish', body: "A working prototype beats a polished proposal. Solve the problem first, then make the solution beautiful." },
    { icon: 'rupee', title: 'Pay what you said', body: "Vendors, interns, partners, on the day we agreed. No 'cash flow' excuses, no 90-day terms when we said 30." },
  ]
  return (
    <section className="relative bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <ChapterHead number="07" label="What we stand for" title="Four lines. We never cross them" />

        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {lines.map((l, i) => (
            <article key={l.title} className={'group relative overflow-hidden rounded-2xl border bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-xl ' + (i === 0 ? 'border-[#38bdf8]/40 bg-[#e6f4ff]/30' : 'border-slate-200')}>
              <div className="flex items-start gap-4">
                <span className={'inline-flex h-12 w-12 flex-none items-center justify-center rounded-xl ' + (i === 0 ? 'bg-[#008eff] text-white' : 'bg-slate-100 text-slate-900')}>
                  <Icon name={l.icon} className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-display text-lg font-bold tracking-tight text-slate-900">{l.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{l.body}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

/* =========================== CHAPTER 08 — Milestones ===================== */
function Milestones() {
  /* The actual company history: founded as Aweteck Services Pvt. Ltd,
     renamed Spectranox in June 2026 once the work had outgrown the old name. */
  const items = [
    {
      y: 'May 2020',
      title: 'Aweteck Services Pvt. Ltd is founded',
      body: 'Registered on 2 May 2020 to build software for clients. A founder, a co-founder, and a services practice.',
    },
    {
      y: 'Jun 2020',
      title: 'The first project ships',
      body: 'Our first client build goes live on 20 June 2020, seven weeks after the company was registered.',
    },
    {
      y: '2021',
      title: 'Skills Uprise begins',
      body: 'A training arm for individuals, teaching the same engineering we do for clients. IT training becomes the second thing we are known for.',
    },
    {
      y: '2022',
      title: 'First enterprise and international client',
      body: 'The first enterprise-grade engagement, and the first project delivered outside India. Longer timelines, harder compliance, and the process that became our fixed-price model.',
    },
    {
      y: '2022 – 2026',
      title: '40+ clients, delivered internationally',
      body: 'Four years of work across borders, spanning web, apps, security, IoT and AI for more than forty clients in ten countries.',
    },
    {
      y: 'Jun 2026',
      title: 'Aweteck becomes Spectranox',
      body: 'The name catches up with the work. One company covering the full spectrum of what we build, under a name that says so.',
    },
  ]
  return (
    <section className="relative bg-[#fdf8f0] py-24 lg:py-32">
      <div className="mx-auto max-w-4xl px-6 lg:px-10">
        <ChapterHead number="08" label="The road so far" title="The road so far. Six years of it" />

        <p className="mx-auto mt-6 max-w-2xl rounded-2xl border border-[#cce9ff] bg-white px-5 py-4 text-center text-sm leading-relaxed text-slate-600">
          Spectranox was founded as{' '}
          <span className="font-semibold text-slate-900">Aweteck Services Pvt. Ltd</span>{' '}
          in May 2020, and renamed Spectranox in June 2026. Same company, same team, same
          registration &mdash; the name simply caught up with the work.
        </p>

        <ol className="mt-14 space-y-5">
          {items.map((it, i) => (
            <li key={it.y} className="group relative grid grid-cols-[auto_1fr] gap-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex flex-col items-center">
                <span className={'inline-flex h-12 w-12 items-center justify-center rounded-xl font-mono text-xs font-bold ' + (i === items.length - 1 ? 'bg-[#008eff] text-slate-900' : 'bg-[#cce9ff] text-[#0062ad]')}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="mt-2 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-500">{it.y}</span>
              </div>
              <div>
                <h3 className="font-display text-base font-bold text-slate-900">{it.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{it.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

/* =========================== CTA — Next chapter ========================== */
function NextChapter() {
  return (
    <section className="relative overflow-hidden bg-[#008eff] py-20 text-white lg:py-28">
      <div aria-hidden className="pointer-events-none absolute -top-32 right-[10%] h-[420px] w-[680px] rounded-full blur-[140px]" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.30), transparent 60%)' }} />
      <div aria-hidden className="pointer-events-none absolute -bottom-20 left-[5%] h-[380px] w-[580px] rounded-full blur-[140px]" style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.30), transparent 60%)' }} />

      <div className="relative mx-auto max-w-4xl px-6 text-center lg:px-10">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-white backdrop-blur">
          The next chapter
        </div>
        <h2 className="mt-5 font-display text-[clamp(2.25rem,4.5vw,3.5rem)] font-bold leading-[1.05] tracking-tight text-white">
          Wanna join the next chapter?
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/90">
          We hire engineers, designers and makers who would rather ship than present. Small team,
          real ownership, and your work in front of clients within weeks of joining.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-bold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-slate-800">
            See open roles
            <Icon name="arrow" className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border-2 border-white bg-white/10 px-6 py-3 text-sm font-bold text-white backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-white hover:text-[#0062ad]">
            Talk to us
          </Link>
        </div>
      </div>
    </section>
  )
}

/* =========================== PAGE ======================================== */
export default function About() {
  return (
    <>
      <Seo
        title="About — Spectranox"
        description="Spectranox is a small, founder-led team building software, hardware, drones and AI for clients across ten countries. Our story, our vision and the terms we work on."
        path="/about"
      />
      <main className="bg-white text-slate-900 antialiased pt-20">
        <VisionMission />
        <Founder />
        <Cofounder />
        <Team />
        <HowWeWork />
        <WeChillHarder />
        <FourLines />
        <Milestones />
        <NextChapter />
      </main>
    </>
  )
}
